import type { Logger } from "@expert/logging";
import type React from "react";

import { useGuideIcons } from "@expert/contentful";
import { Button, Flex, Group, Image, Stack, Text } from "@mantine/core";
import { useMemo } from "react";

import { triggerNextStep } from "../logic-builder";
import { useGuideStore } from "../store";
import classes from "./GuideCard.module.css";

interface GuideSelectionCardProps {
    logger: Logger;
    stepId?: string;
}

export function GuideSelectionCard({ logger: loggerProp, stepId }: GuideSelectionCardProps): React.JSX.Element | null {
    const {
        availableGuides: guides,
        isWaitingForNextStep,
        setIsWaitingForNextStep,
        setOpenGuideSource,
    } = useGuideStore();
    const logger = useMemo(
        () => loggerProp.child({ module: "GuideSelectionCard", supportTeam: "solve" }),
        [loggerProp],
    );

    const isCompactView = guides.length > 2;
    const iconMap = useGuideIcons(guides.map((guide) => guide.icon));

    function handleGuideSelection(guide: string) {
        logger.info(`Solve Guide | Selected flow: ${guide}`);

        setOpenGuideSource({ source: "manual", sourceDetails: undefined });
        triggerNextStep(guide, logger, () => setIsWaitingForNextStep(true));
    }

    return (
        <Stack w="100%" p="md" style={{ flexWrap: "wrap" }}>
            <Group align="flex-start" gap="lg" grow={!isCompactView} w="100%">
                {guides.map((guide) => {
                    const { icon, title } = guide;

                    const iconUrl = iconMap.get(icon);
                    return (
                        <Flex
                            h={isCompactView ? "4rem" : "100%"}
                            justify="center"
                            key={`${stepId}-${title}`}
                            w={isCompactView ? "calc(50% - var(--mantine-spacing-sm))" : undefined}
                        >
                            <Button
                                className={classes.guideSelectionButton}
                                disabled={isWaitingForNextStep}
                                h="100%"
                                onClick={() => handleGuideSelection(title)}
                                p="sm"
                                radius="sm"
                                variant="outline"
                                w="100%"
                            >
                                <Stack align="center" gap="0">
                                    {!isCompactView && iconUrl && <Image h={36} src={iconUrl} w={36} mb="sm" />}
                                    <Text className={classes.selectionButtonText} size="xs">
                                        {title}
                                    </Text>
                                </Stack>
                            </Button>
                        </Flex>
                    );
                })}
            </Group>
        </Stack>
    );
}
